import { useAuthStore } from '@/stores/authStore';
import { usePersonasStore } from '@/stores/personasStore';
import { useTargetsStore } from '@/stores/targetsStore';
import { useNavigate } from 'react-router-dom';
import { Bell, Lightbulb, Megaphone, Network, Sparkles, Target, Users, Zap } from 'lucide-react';
import { useEffect, useMemo } from 'react';

const typeLabels = {
  followed: 'Followed',
  inspirator: 'Inspirators',
  impress: 'Impress targets',
};

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { personas, fetchPersonas, getStats: getPersonaStats } = usePersonasStore();
  const { targets, fetchTargets, getStats: getTargetStats } = useTargetsStore();

  useEffect(() => {
    void fetchPersonas();
    void fetchTargets();
  }, [fetchPersonas, fetchTargets]);

  const personaStats = getPersonaStats();
  const targetStats = getTargetStats();

  const networkByType = useMemo(() => {
    return targets.reduce(
      (acc, target) => {
        const type = target.type ?? 'followed';
        acc[type] += 1;
        return acc;
      },
      { followed: 0, inspirator: 0, impress: 0 },
    );
  }, [targets]);

  const stats = [
    { name: 'Personas', value: String(personaStats.total), icon: Users, change: `${personaStats.defaultCount} default`, tone: 'bg-primary text-primary-foreground' },
    { name: 'Network', value: String(targetStats.total), icon: Network, change: `${targetStats.activeAlerts} alerts on`, tone: 'bg-secondary text-foreground' },
    { name: 'Impress', value: String(targetStats.impressCount), icon: Target, change: 'People to reach', tone: 'status-acid' },
    { name: 'Credits', value: user?.creditsBalance?.toLocaleString() || '0', icon: Sparkles, change: `${user?.plan || 'Free'} plan`, tone: 'bg-foreground text-background' },
  ];

  const latestTargets = targets.slice(0, 4);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="brutalist-panel relative overflow-hidden rounded-3xl p-6 lg:p-8">
        <div className="absolute right-6 top-6 hidden rounded-full border-2 border-border px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground lg:block">
          Manual-posting MVP
        </div>
        <div className="max-w-4xl">
          <p className="brutalist-label">BeLoud Command Center</p>
          <h1 className="mt-3 text-4xl font-black leading-none tracking-[-0.08em] md:text-6xl">
            Grow personas. Track the people that matter.
          </h1>
          <p className="mt-4 max-w-2xl text-base font-medium text-muted-foreground md:text-lg">
            One clear workspace for managed voices, network intelligence, alerts and AI-assisted copy you still publish manually.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            className="rounded-xl border-2 border-border bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.22)] transition-transform hover:-translate-y-0.5 dark:shadow-[4px_4px_0_hsl(var(--primary)/0.24)]"
            onClick={() => navigate('/studio')}
          >
            <Zap className="mr-2 inline h-4 w-4" />
            Create with Studio
          </button>
          <button
            className="rounded-xl border-2 border-border bg-card px-5 py-3 text-sm font-black transition-colors hover:bg-secondary"
            onClick={() => navigate('/targets')}
          >
            Open Network
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.name}
              type="button"
              className="brutalist-panel-soft rounded-2xl p-5 text-left transition-transform hover:-translate-y-1"
              onClick={() => index === 0 ? navigate('/personas') : index === 1 || index === 2 ? navigate('/targets') : navigate('/plans')}
            >
              <div className="mb-5 flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-[0.22em] text-muted-foreground">{stat.name}</span>
                <span className={`rounded-xl border-2 border-border p-2 ${stat.tone}`}>
                  <Icon className="h-4 w-4" />
                </span>
              </div>
              <div className="text-4xl font-black tracking-[-0.08em]">{stat.value}</div>
              <p className="mt-2 text-sm font-bold text-muted-foreground">{stat.change}</p>
            </button>
          );
        })}
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.45fr_0.8fr]">
        <div className="brutalist-panel-soft rounded-3xl p-5 lg:p-6">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="brutalist-label">Persona workspaces</p>
              <h2 className="mt-1 text-2xl font-black tracking-[-0.06em]">Managed voices</h2>
            </div>
            <button className="rounded-xl border-2 border-border px-4 py-2 text-sm font-black hover:bg-secondary" onClick={() => navigate('/personas')}>
              View all
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {personas.slice(0, 4).map((persona) => (
              <button
                key={persona.id}
                className="rounded-2xl border-2 border-border bg-background/45 p-4 text-left transition-colors hover:bg-secondary"
                onClick={() => navigate(`/personas/${persona.id}`)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-primary text-sm font-black text-primary-foreground">
                      {persona.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-black">{persona.name}</p>
                      <p className="truncate text-xs font-bold text-muted-foreground">{persona.tone}</p>
                    </div>
                  </div>
                  <span className="rounded-full border border-border px-2 py-1 text-[11px] font-black uppercase tracking-wide">{persona.language}</span>
                </div>
                <p className="mt-3 line-clamp-2 text-sm font-medium text-muted-foreground">
                  {persona.positioning || persona.style_notes || 'Voice and positioning not yet completed.'}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs font-black">
                  <span className="rounded-full border border-border px-2 py-1">{persona.targets_count ?? persona.network_count ?? 0} people</span>
                  {persona.is_default ? <span className="status-acid rounded-full border border-border px-2 py-1">Default</span> : null}
                </div>
              </button>
            ))}
            {personas.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-border p-6 text-sm font-bold text-muted-foreground">
                Create your first managed persona to unlock a workspace.
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-4">
          <div className="brutalist-panel-soft rounded-3xl p-5 lg:p-6">
            <p className="brutalist-label">Network intelligence</p>
            <h2 className="mt-1 text-2xl font-black tracking-[-0.06em]">Relationship map</h2>
            <div className="mt-5 space-y-3">
              {Object.entries(networkByType).map(([type, count]) => (
                <button key={type} className="flex w-full items-center justify-between rounded-2xl border-2 border-border bg-background/45 p-4 text-left hover:bg-secondary" onClick={() => navigate('/targets')}>
                  <div className="flex items-center gap-3">
                    <span className="rounded-xl border-2 border-border bg-primary/10 p-2 text-primary">
                      {type === 'inspirator' ? <Lightbulb className="h-4 w-4" /> : type === 'impress' ? <Megaphone className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                    </span>
                    <span className="font-black">{typeLabels[type as keyof typeof typeLabels]}</span>
                  </div>
                  <span className="text-2xl font-black tracking-[-0.06em]">{count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="brutalist-panel-soft rounded-3xl p-5 lg:p-6">
            <p className="brutalist-label">Latest people</p>
            <div className="mt-4 space-y-3">
              {latestTargets.length ? latestTargets.map((target) => (
                <div key={target.id} className="flex items-center justify-between gap-3 border-b border-border pb-3 last:border-b-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-black">{target.nickname || target.full_name || 'LinkedIn profile'}</p>
                    <p className="truncate text-xs font-bold text-muted-foreground">{target.type ?? 'followed'} · {target.alert_frequency ?? 'daily'}</p>
                  </div>
                  <span className={`h-3 w-3 rounded-full border border-border ${(target.notify_on_post || target.notify_on_comment) ? 'bg-lime-300' : 'bg-muted'}`} />
                </div>
              )) : <p className="text-sm font-bold text-muted-foreground">No monitored people yet.</p>}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
